import { useBreakpointDown } from '@travel-frontend/core';
import { isEqual } from 'lodash';
import { useCallback, useState } from 'react';
import * as ActionTypes from '../../../../../components/SearchForm/components/TravellersFieldMultiroom/actionTypes.js';
import { useWidgetOverlay } from '../../../../../components/SearchForm/hooks/useWidgetOverlay.js';
const aggregateAdultsCount = rooms => {
  return rooms.reduce((count, room) => {
    count += room.adultsCount;
    return count;
  }, 0);
};
const aggregateChildrenCount = rooms => {
  return rooms.reduce((count, room) => {
    count += room.childrenAges.length;
    return count;
  }, 0);
};
export const useTravellersFieldMultiroom = ({
  value: controlledValue,
  defaultValue,
  config,
  floatingOptions,
  invalid = false,
  disabled = false,
  dirty = false,
  dimming = false,
  dispatch
}) => {
  const mdDown = useBreakpointDown('md');
  const [value, setValue] = useState(controlledValue);
  const widgetType = mdDown ? 'drawer' : 'popover';
  const [widgetOpen, setWidgetOpen] = useState(false);
  const {
    resetBehavior = 'previous',
    required,
    adultsCountMin,
    adultsCountMax,
    adultsCountMinPerRoom,
    childrenAgeMin,
    childrenAgeMax,
    childrenCountMin,
    childrenCountMax,
    widgetHead,
    travellersCountMax
  } = config;
  const isValidChildrenAge = useCallback(childrenAge => {
    return childrenAge !== null && childrenAge >= childrenAgeMin && childrenAge <= childrenAgeMax;
  }, [childrenAgeMax, childrenAgeMin]);
  const isValidValue = useCallback(value => {
    return (
      // must not exceed maximum number of possible travellers
      value.childrenCount + value.adultsCount <= travellersCountMax &&
      // must have at least minmimun number of adults
      value.adultsCount >= adultsCountMin &&
      // must not exceed maximum number of possible adults
      value.adultsCount <= adultsCountMax &&
      // must have at least minmimun number of children
      value.childrenCount >= childrenCountMin &&
      // must not exceed maximum number of possible children
      value.childrenCount <= childrenCountMax &&
      // every room must have at least a minimum number of adults
      // and every child must have a valid age
      value.rooms.every(room => room.adultsCount >= adultsCountMinPerRoom && room.childrenAges.every(isValidChildrenAge))
    );
  }, [isValidChildrenAge, adultsCountMax, adultsCountMin, childrenCountMax, childrenCountMin, travellersCountMax, adultsCountMinPerRoom]);
  const dispatchEvent = useCallback(({
    type,
    value
  }) => {
    let isDefaultPrevented = false;
    dispatch({
      type,
      payload: {
        value,
        valid: isValidValue(value)
      },
      preventDefault: () => {
        isDefaultPrevented = true;
      }
    });
    return isDefaultPrevented;
  }, [dispatch, isValidValue]);
  const handleOpenChange = useCallback((open, origin) => {
    let isDefaultPrevented = false;
    const valueChanged = !isEqual(value, controlledValue);
    if (valueChanged && origin === 'close-button') {
      dispatchEvent({
        type: ActionTypes.CloseClicked,
        value: controlledValue
      });
      setValue(controlledValue);
    }
    if (valueChanged && (origin === 'apply-button' || !open && widgetType === 'popover')) {
      isDefaultPrevented = dispatchEvent({
        type: ActionTypes.ValueChanged,
        value
      });
    }
    if (!isDefaultPrevented) {
      setWidgetOpen(open);
    }
  }, [controlledValue, dispatchEvent, value, widgetType]);
  const overlay = useWidgetOverlay({
    open: widgetOpen,
    type: widgetType,
    options: floatingOptions,
    onOpenChange: handleOpenChange,
    layout: 'full',
    dimming
  });
  const handleApply = useCallback(() => {
    handleOpenChange(false, 'apply-button');
  }, [handleOpenChange]);
  const handleClose = useCallback(() => {
    handleOpenChange(false, 'close-button');
  }, [handleOpenChange]);
  const handleReset = useCallback(() => {
    dispatchEvent({
      type: ActionTypes.ResetClicked,
      value: controlledValue
    });
    setValue(resetBehavior === 'default' && defaultValue ? defaultValue : controlledValue);
  }, [controlledValue, defaultValue, dispatchEvent, resetBehavior]);
  const handleAdultsCountChange = useCallback((roomIndex, adultsCount) => {
    const rooms = Array.from(value.rooms);
    rooms[roomIndex] = {
      ...rooms[roomIndex],
      adultsCount
    };
    const newValue = {
      adultsCount: aggregateAdultsCount(rooms),
      childrenCount: aggregateChildrenCount(rooms),
      rooms
    };
    dispatchEvent({
      type: ActionTypes.AdultsCountChanged,
      value: newValue
    });
    setValue(newValue);
  }, [dispatchEvent, value.rooms]);
  const handleChildrenCountChange = useCallback((roomIndex, childrenCount) => {
    const rooms = Array.from(value.rooms);
    rooms[roomIndex] = {
      ...rooms[roomIndex],
      childrenAges: Array.from(rooms[roomIndex].childrenAges)
    };
    if (childrenCount > rooms[roomIndex].childrenAges.length) {
      // new child was added
      rooms[roomIndex].childrenAges.push(null);
    } else {
      // child was removed
      rooms[roomIndex].childrenAges.pop();
    }
    const newValue = {
      adultsCount: aggregateAdultsCount(rooms),
      childrenCount: aggregateChildrenCount(rooms),
      rooms
    };
    dispatchEvent({
      type: ActionTypes.ChildrenCountChanged,
      value: newValue
    });
    setValue(newValue);
  }, [dispatchEvent, value.rooms]);
  const handleRoomAdd = useCallback(() => {
    const rooms = [...value.rooms, {
      adultsCount: adultsCountMinPerRoom,
      childrenAges: []
    }];
    const newValue = {
      childrenCount: aggregateChildrenCount(rooms),
      adultsCount: aggregateAdultsCount(rooms),
      rooms
    };
    dispatchEvent({
      type: ActionTypes.RoomAdded,
      value: newValue
    });
    setValue(newValue);
  }, [adultsCountMinPerRoom, dispatchEvent, value.rooms]);
  const handleRoomRemove = useCallback(roomIndex => {
    const rooms = value.rooms.filter((_room, index) => index !== roomIndex);
    const newValue = {
      adultsCount: aggregateAdultsCount(rooms),
      childrenCount: aggregateChildrenCount(rooms),
      rooms
    };
    dispatchEvent({
      type: ActionTypes.RoomRemoved,
      value: newValue
    });
    setValue(newValue);
  }, [dispatchEvent, value.rooms]);
  const handleChildAgeChange = useCallback((roomIndex, childIndex, childAge) => {
    const newValue = {
      adultsCount: value.adultsCount,
      childrenCount: value.childrenCount,
      rooms: value.rooms.map(room => {
        return {
          adultsCount: room.adultsCount,
          childrenAges: Array.from(room.childrenAges)
        };
      })
    };
    newValue.rooms[roomIndex].childrenAges[childIndex] = childAge;
    dispatchEvent({
      type: ActionTypes.ChildrenAgeChanged,
      value: newValue
    });
    setValue(newValue);
  }, [dispatchEvent, value]);
  const getFieldProps = useCallback(() => ({
    ...overlay.getReferenceProps(),
    value,
    active: widgetOpen
  }), [overlay, value, widgetOpen]);
  const getWidgetProps = () => ({
    ...overlay.getWidgetProps(),
    type: widgetType,
    open: widgetOpen,
    data: value,
    hasMissingChildrenAgeMessage: invalid,
    childrenAgesValidationState: value.rooms.map(room => room.childrenAges.map(isValidChildrenAge)),
    applyButtonDisabled: dirty,
    handlers: {
      onReset: handleReset,
      onApply: handleApply,
      onClose: handleClose,
      onAdultsCountChange: handleAdultsCountChange,
      onChildAgeChange: handleChildAgeChange,
      onChildrenCountChange: handleChildrenCountChange,
      onRoomAdd: handleRoomAdd,
      onRoomRemove: handleRoomRemove
    },
    config,
    dimming,
    widgetHead
  });
  const setValueHandler = useCallback(nextValue => {
    if (!isEqual(value, nextValue)) {
      dispatchEvent({
        type: ActionTypes.FunctionValueChanged,
        value: nextValue
      });
      setValue(nextValue);
    }
  }, [dispatchEvent, value]);
  const checkValidity = useCallback(() => required === false || isValidValue(value), [isValidValue, required, value]);
  return {
    isActive: widgetOpen,
    isInvalid: invalid,
    isDisabled: disabled,
    getFieldProps,
    getWidgetProps,
    setValue: setValueHandler,
    checkValidity
  };
};